<template>
  <Modal
    ref="modal"
    hide-close-button
    :modal-style="{ width: '40vw', 'max-height': '100%' }"
    title="Bulk Confirm/Revert"
  >
    <QForm>
      <QTabs v-model="tab">
        <QTab
          label="By organisation"
          name="organisation"
        />
        <QTab
          label="By brief"
          name="brief"
        />
        <QTab
          label="By user"
          name="user"
        />
      </QTabs>
      <QTabPanels
        v-model="tab"
        animated
        class="full-width"
      >
        <QTabPanel name="organisation">
          <OrganisationsDropdown
            v-model="filter.organisationId"
            class="organisation-select"
            filled
            label="Select Organisation"
            stack-label
            use-chips
            @update:model-value="loadFields"
          />
        </QTabPanel>
        <QTabPanel name="brief">
          <OrganisationsDropdown
            v-model="filter.organisationId"
            class="organisation-select"
            filled
            label="Select Organisation"
            stack-label
            use-chips
            @update:model-value="loadFields"
          />

          <SelectList
            v-model="filter.briefId"
            class="q-pb-md q-mt-xs"
            clearable
            :disable="!hasOrganisation"
            emit-value
            label="Select Brief"
            option-label="label"
            option-value="value"
            :options="briefs"
          />
        </QTabPanel>
        <QTabPanel name="user">
          <OrganisationsDropdown
            v-model="filter.organisationId"
            class="organisation-select"
            filled
            label="Select Organisation"
            stack-label
            use-chips
            @update:model-value="loadFields"
          />

          <SelectList
            v-model="filter.userId"
            class="q-pb-md q-mt-xs"
            clearable
            :disable="!hasOrganisation"
            emit-value
            label="Select User"
            option-label="name"
            :options="users"
          />
        </QTabPanel>
      </QTabPanels>

      <QCardActions
        align="right"
        class="q-mr-sm"
      >
        <QBtn
          color="green"
          :disabled="!canBulkItems"
          icon="thumb_up"
          label="Bulk Confirm"
          @click="openBulkReviewWarning('confirm')"
        />
        <QBtn
          color="red"
          :disabled="!canBulkItems"
          icon="thumb_down"
          label="Bulk Revert"
          @click="openBulkReviewWarning('revert')"
        />
      </QCardActions>
    </QForm>

    <template #buttons="{ close }">
      <a
        class="q-mr-md text-black"
        href="#"
        @click.prevent="close"
      >
        Close
      </a>
    </template>
  </Modal>
</template>

<script>
import { Notify } from "quasar";

import Modal from "shared/components/core/modals/Modal.vue";
import { User } from "shared/resources";

import OrganisationsDropdown from "src/components/common/OrganisationsDropdown.vue";
import SelectList from "src/components/common/SelectList.vue";

export default {
  name: "BulkExcludedMentionsModal",
  components: {
    Modal,
    SelectList,
    OrganisationsDropdown,
  },
  props: {
    openTab: {
      type: String,
      default: "organisation",
    },
  },
  data() {
    return {
      briefs: [],
      filter: {
        briefId: null,
        organisationId: null,
        userId: null,
      },
      users: [],
      tab: this.openTab,
    };
  },
  computed: {
    canBulkItems() {
      return (
        (this.tab === "organisation" && this.filter.organisationId !== null) ||
        (this.tab === "brief" && this.filter.briefId !== null) ||
        (this.tab === "user" && this.filter.userId !== null)
      );
    },
    hasOrganisation() {
      return this.filter.organisationId;
    },
  },
  watch: {
    tab: {
      immediate: true,
      handler() {
        this.resetFields();
      },
    },
  },
  methods: {
    async loadUsers() {
      try {
        const filters = {
          organisationId: this.filter.organisationId,
          active: { eq: true },
        };

        const response = await User.select(["name"])
          .where(filters)
          .order({ firstName: "asc", lastName: "asc" })
          .per(999)
          .all();

        this.users = response.data;
      } catch (error) {
        this.$error("There was an error loading users", error);
      }
    },
    async loadBriefs() {
      try {
        const response = await this.$streemApiAdmin.get(
          `organisations/${this.filter.organisationId}/briefs`
        );

        this.rawBriefs = response.data;

        this.briefs = this.rawBriefs.map((option) => ({
          label: `[${option.id}] ${option.label || ""}`,
          value: option.id,
        }));
      } catch (error) {
        this.$error(
          "There was an error loading the organisation briefs",
          error
        );
      }
    },
    loadFields() {
      this.resetFields();

      if (!this.filter.organisationId) {
        return;
      }

      this.loadUsers();
      this.loadBriefs();
    },
    resetFields() {
      this.filter.userId = null;
      this.filter.briefId = null;
      this.filter.users = [];
      this.filter.briefs = [];
    },
    openBulkReviewWarning(action) {
      this.$q
        .dialog({
          title: "Confirm bulk review",
          message: `Are you sure you want to ${action} these excluded mentions?`,
          persistent: true,
          cancel: "Cancel",
        })
        .onOk(() => {
          this.bulkReviewMentions(action);
        });
    },
    async bulkReviewMentions(action) {
      const disable = action === "revert";

      try {
        this.$q.loading.show({ delay: 500 });

        await this.$streemApiAdmin.put("excluded_mentions/bulk_review", {
          params: {
            brief_id: this.filter.briefId,
            organisation_id: this.filter.organisationId,
            user_id: this.filter.userId,
            disable,
          },
        });

        this.$refs.modal.close();

        Notify.create({
          message: `Excluded mentions have been ${
            disable ? "reverted" : "confirmed"
          }.`,
          color: "primary",
        });
      } catch (error) {
        Notify.create({
          message: `There was an error ${
            disable ? "reverting" : "confirming"
          } these excluded mentions. ${error.message}`,
          color: "red",
        });
      } finally {
        this.$q.loading.hide();
      }
    },
  },
};
</script>
